import {
  isHomeCarouselType,
  type HomeCarouselType,
} from '@/common/types/home-carousel.type';
import { SHOP_PLP_PATH } from '@/features/shop/data/shop-routes';

export const plpSortOptions = [
  { id: 'newest', label: 'جدیدترین' },
  { id: 'cheapest', label: 'ارزان‌ترین' },
  { id: 'expensive', label: 'گران‌ترین' },
] as const;

export type PlpSortId = (typeof plpSortOptions)[number]['id'];

export const PLP_RECOMMENDED_LISTING = 'recommended' as const;
export type PlpListing = typeof PLP_RECOMMENDED_LISTING;

export interface PlpFilterState {
  available: boolean;
  freeShipping: boolean;
  discounted: boolean;
  category?: string;
  minimumPrice: number;
  maximumPrice: number;
  colors: string[];
  sizes: string[];
  weights: string[];
}

export interface PlpUrlState {
  page: number;
  sort: PlpSortId;
  carousel?: HomeCarouselType;
  listing?: PlpListing;
  filters: PlpFilterState;
}

export type PlpSearchParams = Record<string, string | string[] | undefined>;

export const plpMaximumPrice = 100_000_000;
export const plpPageSize = 20;

export const defaultPlpFilters: PlpFilterState = {
  available: false,
  freeShipping: false,
  discounted: false,
  minimumPrice: 0,
  maximumPrice: plpMaximumPrice,
  colors: [],
  sizes: [],
  weights: [],
};

export const defaultPlpUrlState: PlpUrlState = {
  page: 1,
  sort: 'newest',
  filters: defaultPlpFilters,
};

const stateKeys = [
  'page',
  'sort',
  'carousel',
  'listing',
  'available',
  'freeShipping',
  'discounted',
  'category',
  'minPrice',
  'maxPrice',
  'color',
  'size',
  'weight',
];

const firstValue = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;
const arrayValue = (value: string | string[] | undefined) =>
  value === undefined ? [] : Array.isArray(value) ? value : [value];
const boundedNumber = (
  value: string | undefined,
  fallback: number,
  minimum: number,
  maximum: number
) => {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? Math.min(maximum, Math.max(minimum, Math.round(parsed)))
    : fallback;
};

export function parsePlpUrlState(params: PlpSearchParams): PlpUrlState {
  const sortValue = firstValue(params.sort);
  const normalizedSort =
    sortValue === 'relevant' ? defaultPlpUrlState.sort : sortValue;
  const sort =
    plpSortOptions.find((option) => option.id === normalizedSort)?.id ??
    defaultPlpUrlState.sort;
  const minimumPrice = boundedNumber(
    firstValue(params.minPrice),
    0,
    0,
    plpMaximumPrice
  );
  const maximumPrice = boundedNumber(
    firstValue(params.maxPrice),
    plpMaximumPrice,
    minimumPrice,
    plpMaximumPrice
  );

  const carouselValue = firstValue(params.carousel);
  const listingValue = firstValue(params.listing);

  return {
    page: boundedNumber(firstValue(params.page), 1, 1, 999),
    sort,
    carousel: isHomeCarouselType(carouselValue) ? carouselValue : undefined,
    listing: listingValue === PLP_RECOMMENDED_LISTING ? listingValue : undefined,
    filters: {
      available: firstValue(params.available) === '1',
      freeShipping: firstValue(params.freeShipping) === '1',
      discounted: firstValue(params.discounted) === '1',
      category: firstValue(params.category) || undefined,
      minimumPrice,
      maximumPrice,
      colors: arrayValue(params.color),
      sizes: arrayValue(params.size),
      weights: arrayValue(params.weight),
    },
  };
}

export function plpStateToSearchParams(
  state: PlpUrlState,
  current?: URLSearchParams
) {
  const params = new URLSearchParams(current?.toString());
  stateKeys.forEach((key) => {
    params.delete(key);
  });
  if (state.page > 1) params.set('page', String(state.page));
  if (state.sort !== defaultPlpUrlState.sort) params.set('sort', state.sort);
  if (state.carousel) params.set('carousel', state.carousel);
  if (state.listing) params.set('listing', state.listing);
  if (state.filters.available) params.set('available', '1');
  if (state.filters.freeShipping) params.set('freeShipping', '1');
  if (state.filters.discounted) params.set('discounted', '1');
  if (state.filters.category) params.set('category', state.filters.category);
  if (state.filters.minimumPrice > 0)
    params.set('minPrice', String(state.filters.minimumPrice));
  if (state.filters.maximumPrice < plpMaximumPrice)
    params.set('maxPrice', String(state.filters.maximumPrice));
  state.filters.colors.forEach((color) => {
    params.append('color', color);
  });
  state.filters.sizes.forEach((size) => {
    params.append('size', size);
  });
  state.filters.weights.forEach((weight) => {
    params.append('weight', weight);
  });
  return params;
}

export function createPlpHref(state: PlpUrlState) {
  const query = plpStateToSearchParams(state).toString();
  return query ? `${SHOP_PLP_PATH}?${query}` : SHOP_PLP_PATH;
}

export function createRecommendedPlpHref() {
  return createPlpHref({
    ...defaultPlpUrlState,
    listing: PLP_RECOMMENDED_LISTING,
  });
}

export function plpSearchParamsToString(params: PlpSearchParams) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        query.append(key, item);
      });
    } else if (value !== undefined) {
      query.append(key, value);
    }
  });
  return query.toString();
}

/**
 * True when the raw URL has unknown keys or non-canonical values that should
 * be redirected. Param *order* alone must not trigger a redirect — that remounts
 * the client and breaks hybrid infinite scroll.
 */
export function needsPlpUrlNormalization(raw: PlpSearchParams) {
  const state = parsePlpUrlState(raw);
  const canonical = plpStateToSearchParams(state);
  const allowed = new Set(stateKeys);

  for (const key of Object.keys(raw)) {
    if (!allowed.has(key)) return true;
  }

  for (const key of stateKeys) {
    const canonicalValues = canonical.getAll(key).slice().sort().join('\0');
    const rawValues = arrayValue(raw[key]).slice().sort().join('\0');
    const rawPresent = raw[key] !== undefined && rawValues.length > 0;
    const canonPresent = canonical.has(key);

    if (rawPresent !== canonPresent) return true;
    if (rawPresent && rawValues !== canonicalValues) return true;
  }

  return false;
}

export function hasActivePlpFacets(state: PlpUrlState) {
  const { filters } = state;
  return (
    state.sort !== defaultPlpUrlState.sort ||
    filters.available ||
    filters.freeShipping ||
    filters.discounted ||
    Boolean(filters.category) ||
    filters.minimumPrice !== defaultPlpFilters.minimumPrice ||
    filters.maximumPrice !== defaultPlpFilters.maximumPrice ||
    filters.colors.length > 0 ||
    filters.sizes.length > 0 ||
    filters.weights.length > 0
  );
}
